import React, { useRef, useState } from 'react';
import {
  Box, Button, Checkbox, FormControl, FormHelperText, FormErrorMessage, FormLabel, HStack, Input,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Text, useId, VStack,
} from '@chakra-ui/react';
import { CloseIcon } from './AdminIcon';
import AdminState from './AdminState';
import SessionDurationLabel from './SessionDurationLabel';
import useAdminToast from './useAdminToast';
import Backend from '../../utils/api';
import { useAnimationToggle } from '../../Contexts/AnimationContext';

export default function PurposeEditorDialog({ purpose, user, labId, onClose, onSaved, finalFocusRef }) {
  const editing = Boolean(purpose);
  const totalMinutes = Number(purpose?.time_interval) || 0;
  const [name, setName] = useState(purpose?.custom_reason || '');
  const [hours, setHours] = useState(String(Math.floor(totalMinutes / 60)));
  const [minutes, setMinutes] = useState(String(totalMinutes % 60));
  const [emailReminder, setEmailReminder] = useState(purpose?.enable_email_reminder !== false);
  const [timeTracking, setTimeTracking] = useState(purpose?.enable_time_tracking !== false);
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [saveError, setSaveError] = useState('');
  const pendingRef = useRef(false);
  const nameRef = useRef(null);
  const hoursRef = useRef(null);
  const formId = useId();
  const toast = useAdminToast();
  const { animationsEnabled } = useAnimationToggle();
  const duration = Number(hours) * 60 + Number(minutes);
  const nameError = name.trim() ? '' : 'Check-in purpose is required.';
  const durationError = hours === '' || minutes === '' ? 'Enter hours and minutes.' :
    !Number.isInteger(Number(hours)) || Number(hours) < 0 ? 'Hours must be a whole number of 0 or more.' :
    !Number.isInteger(Number(minutes)) || Number(minutes) < 0 || Number(minutes) > 59 ? 'Minutes must be a whole number from 0 to 59.' :
    !Number.isSafeInteger(duration) || duration <= 0 ? 'Session duration must be greater than 0 minutes.' : '';
  const showDurationError = submitted && Boolean(durationError);
  const close = () => { if (!pendingRef.current) onClose(); };
  const submit = async event => {
    event.preventDefault();
    if (pendingRef.current) return;
    setSubmitted(true);
    if (nameError || durationError) {
      (nameError ? nameRef : hoursRef).current?.focus();
      return;
    }
    pendingRef.current = true;
    setPending(true);
    setSaveError('');
    try {
      const response = editing ? await Backend.editReason(user.UID, purpose.id, name.trim(), duration, emailReminder, timeTracking) :
        await Backend.newReason(user.UID, name.trim(), duration, labId, emailReminder, timeTracking);
      if (!response || response.error || ![200, 201].includes(response.status)) {
        throw new Error(response?.status === 409 ? 'A check-in purpose with this name already exists for this space. Choose a different name.' :
          'Could not save this check-in purpose. Try again.');
      }
      toast({ title: editing ? 'Check-in purpose saved' : 'Check-in purpose created' });
      onSaved();
    } catch (error) {
      setSaveError(error.message === 'A check-in purpose with this name already exists for this space. Choose a different name.' ? error.message :
        'Could not save this check-in purpose. Try again.');
      pendingRef.current = false;
      setPending(false);
    }
  };
  return <Modal isOpen onClose={close} size="lg" isCentered scrollBehavior="inside"
    initialFocusRef={nameRef} finalFocusRef={finalFocusRef} closeOnEsc={!pending} closeOnOverlayClick={!pending}
    motionPreset={animationsEnabled ? 'scale' : 'none'}>
    <ModalOverlay />
    <ModalContent as="form" noValidate onSubmit={submit}>
      <ModalHeader>{editing ? 'Edit check-in purpose' : 'Add check-in purpose'}</ModalHeader>
      <ModalCloseButton aria-label="Close purpose editor" isDisabled={pending}><CloseIcon /></ModalCloseButton>
      <ModalBody>
        <VStack spacing={5} align="stretch">
          <FormControl isInvalid={submitted && Boolean(nameError)} isDisabled={pending} isRequired>
            <FormLabel htmlFor={`${formId}-purpose`}>Check-in purpose</FormLabel>
            <Input ref={nameRef} id={`${formId}-purpose`} value={name} onChange={event => setName(event.target.value)}
              placeholder="For example, a class project" />
            {submitted && nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
          </FormControl>
          <Box as="fieldset" border={0} p={0} m={0} minW={0} disabled={pending}>
            <Box as="legend" mb={3} fontSize="sm" fontWeight="semibold"><SessionDurationLabel /></Box>
            <HStack spacing={4} align="start">
              <FormControl w="112px" minW={0} flex="0 1 112px" isInvalid={showDurationError} isDisabled={pending}>
                <FormLabel htmlFor={`${formId}-hours`}>Hours</FormLabel>
                <Input ref={hoursRef} id={`${formId}-hours`} type="number" inputMode="numeric" min={0} step={1}
                  value={hours} onChange={event => setHours(event.target.value)}
                  aria-describedby={showDurationError ? `${formId}-duration-error` : undefined} />
              </FormControl>
              <FormControl w="112px" minW={0} flex="0 1 112px" isInvalid={showDurationError} isDisabled={pending}>
                <FormLabel htmlFor={`${formId}-minutes`}>Minutes</FormLabel>
                <Input id={`${formId}-minutes`} type="number" inputMode="numeric" min={0} max={59} step={1}
                  value={minutes} onChange={event => setMinutes(event.target.value)}
                  aria-describedby={showDurationError ? `${formId}-duration-error` : undefined} />
              </FormControl>
            </HStack>
            {showDurationError && <Text id={`${formId}-duration-error`} role="alert" color="red.600" fontSize="sm" mt={2}>{durationError}</Text>}
          </Box>
          <VStack as="fieldset" border={0} p={0} m={0} minW={0} spacing={3} align="stretch" disabled={pending}>
            <FormControl isDisabled={pending}>
              <Checkbox isChecked={emailReminder} onChange={event => setEmailReminder(event.target.checked)}>
                Enable email reminder
              </Checkbox>
              <FormHelperText>Send a reminder email when someone checking in for this purpose forgets to check out.</FormHelperText>
            </FormControl>
            <FormControl isDisabled={pending}>
              <Checkbox isChecked={timeTracking} onChange={event => setTimeTracking(event.target.checked)}>
                Enable time tracking
              </Checkbox>
              <FormHelperText>Credit the session duration above if someone forgets to check out. Turn off to record the time at the nightly sweep instead.</FormHelperText>
            </FormControl>
          </VStack>
          {saveError && <AdminState status="error" title="Check-in purpose could not be saved" description={saveError} mb={0} />}
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={close} isDisabled={pending}>Cancel</Button>
        <Button type="submit" colorScheme="blue" isLoading={pending} loadingText={editing ? 'Saving…' : 'Creating…'}>{editing ? 'Save' : 'Create'}</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>;
}
